import {
  PRECOMPTE_LOYER,
  PRECOMPTE_HONORAIRES_NR,
  PRECOMPTE_HONORAIRES_RES,
  PRECOMPTE_PRESTATIONS,
} from '@shared/constants/Taux';
import { roundFCFA } from '@shared/utils/round';
import type { PrecompteType } from '@shared/types/Facture';
import {
  PARAMETRES_FISCAUX_DATES,
  parametreFiscalAu,
  type ParametreFiscalDate,
} from '@shared/constants/ParametresFiscauxDates';

export interface OptionsPrecompte {
  /** Date de l'opération (AAAA-MM-JJ). Sans date, la table de paramètres datés n'est pas consultée (taux historique). */
  readonly date?: string;
  /** Table de paramètres fiscaux datés (défaut : `PARAMETRES_FISCAUX_DATES`, seules les lignes actives comptent). */
  readonly parametres?: readonly ParametreFiscalDate[];
}

/**
 * Helper centralisant les taux officiels de précompte et de retenue (règle de la source la plus récente, 18/10/2026) :
 * - loyer : 5 % (CGI art. 178 bis, ligne datée active depuis 2017-01-01 ; 10 % historique sans date) ;
 * - honoraires_nr : 25 % (CGI 2025 art. 206) ;
 * - honoraires_res et prestations : 9,5 % (CGI 2025 art. 23), qui ne vise que les prestataires NON assujettis à la TVA.
 * Empêche toute dérive utilisateur sur les taux.
 */
export class PrecompteISService {
  static tauxFor(type: PrecompteType, options: OptionsPrecompte = {}): number {
    switch (type) {
      case 'loyer': {
        /** Retenue sur loyers (art. 178 bis) : la ligne datée ACTIVE (5 % depuis 2017) remplace le 10 % historique. */
        const ligne = options.date
          ? parametreFiscalAu('retenue_loyers_178bis', options.date, options.parametres ?? PARAMETRES_FISCAUX_DATES)
          : undefined;
        return ligne ? ligne.valeur : PRECOMPTE_LOYER;
      }
      case 'honoraires_nr':
        return PRECOMPTE_HONORAIRES_NR;
      case 'honoraires_res':
        return PRECOMPTE_HONORAIRES_RES;
      case 'prestations':
        return PRECOMPTE_PRESTATIONS;
      default:
        return 0;
    }
  }

  static calculer(
    total_ht: number,
    type: PrecompteType,
    options: OptionsPrecompte = {},
  ): { taux: number; montant: number } {
    const taux = this.tauxFor(type, options);
    return { taux, montant: roundFCFA(total_ht * taux) };
  }
}
